import { useEffect, useState } from "react";
import api, { money, fmtHours, fmtDate } from "../lib/api";
import { Card } from "../components/ui/card";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, Legend, CartesianGrid,
  AreaChart, Area, PieChart, Pie, Cell,
} from "recharts";
import {
  TrendingUp, Clock, Wallet, AlertTriangle, CheckCircle2, Receipt, FileText, Timer, Target, Trophy, Medal,
  Award, Percent, CalendarDays, Users, Building2, Activity, BarChart3, PieChart as PieChartIcon, X,
} from "lucide-react";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";

const KpiCard = ({ label, value, icon: Icon, tone = "blue", testid }) => (
  <Card className="p-5 flex items-start justify-between" data-testid={testid}>
    <div>
      <div className="text-xs uppercase tracking-wider text-slate-500 font-medium">{label}</div>
      <div className="mt-2 font-heading text-2xl font-bold text-slate-900">{value}</div>
    </div>
    <div className={`w-10 h-10 rounded-md flex items-center justify-center bg-${tone}-100 text-${tone}-700`}>
      <Icon size={18} />
    </div>
  </Card>
);

// EXPERIMENTAL (delta 2026-09-17) — moldura única para os 6 gráficos do
// Dashboard: título/ícone consistentes + os 4 estados exigidos (item 12
// do pedido) — carregando, sem dados, erro, e o gráfico de verdade
// (inclusive com dado parcial, ex.: só 1 mês com valor > 0). Nunca
// desenha um gráfico "falso" (eixo/legenda vazios fingindo dado real)
// quando não há nada a mostrar.
// COMMIT D (delta 2026-09-18) — refinamento SÓ visual, a pedido do
// usuário, comparado às imagens de referência: cards mais claros/suaves
// (borda discreta, sombra mínima), mais respiro (padding maior, título
// com mais destaque), gráfico um pouco menor (200px, era 220px) — nunca
// mudou a origem/agregação dos dados, só a moldura/tipografia.
function ChartCard({ icon: Icon, title, subtitle, loading, error, empty, emptyMessage, testId, children }) {
  return (
    <Card className="p-6 border-slate-100 shadow-sm rounded-2xl" data-testid={testId}>
      <div className="flex items-center gap-2.5 mb-1">
        <div className="w-9 h-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center shrink-0"><Icon size={17} /></div>
        <div className="font-heading font-bold text-[15px] text-slate-800">{title}</div>
      </div>
      {subtitle && <p className="text-xs text-slate-400 mb-3 ml-[46px]">{subtitle}</p>}
      {loading ? (
        <div className="h-[200px] flex items-center justify-center text-sm text-slate-400" data-testid={`${testId}-loading`}>Carregando...</div>
      ) : error ? (
        <div className="h-[200px] flex items-center justify-center text-sm text-red-500 text-center px-4" data-testid={`${testId}-error`}>
          Não foi possível carregar este gráfico.
        </div>
      ) : empty ? (
        <div className="h-[200px] flex items-center justify-center text-sm text-slate-400 text-center px-4" data-testid={`${testId}-empty`}>
          {emptyMessage || "Sem dados no período."}
        </div>
      ) : (
        <div style={{ width: "100%", height: 200 }}>{children}</div>
      )}
    </Card>
  );
}

const statusPt = (s) => ({
  em_andamento: "Em andamento", concluida: "Concluída",
  rejeitada_supervisor: "Rejeitada (supervisor)", rejeitada_cliente: "Rejeitada (cliente)",
  aguardando_assinatura: "Aguardando assin.",
  aguardando_faturamento: "Aguardando fat.", faturada: "Faturada", cancelada: "Cancelada",
}[s] || s);

const statusColor = (s) => ({
  em_andamento: "bg-blue-100 text-blue-800",
  concluida: "bg-emerald-100 text-emerald-800",
  rejeitada_supervisor: "bg-red-100 text-red-800",
  rejeitada_cliente: "bg-red-100 text-red-800",
  aguardando_assinatura: "bg-cyan-100 text-cyan-800",
  aguardando_faturamento: "bg-indigo-100 text-indigo-800",
  faturada: "bg-slate-200 text-slate-800",
  cancelada: "bg-red-100 text-red-800",
}[s] || "bg-slate-100 text-slate-700");

// EXPERIMENTAL (delta 2026-09-17) — Dashboard analytics. Mesma paleta
// semântica de statusColor acima, só convertida para hex porque Recharts
// (Pie/Cell) precisa de uma cor sólida, não de uma classe Tailwind.
// Nenhuma cor nova inventada — reaproveita os mesmos tons já usados nos
// gráficos existentes (#2563EB/#10B981) e nos badges de status do
// sistema (blue/emerald/red/cyan/indigo/slate).
const STATUS_HEX = {
  em_andamento: "#2563EB", concluida: "#10B981", rejeitada_supervisor: "#DC2626", rejeitada_cliente: "#EF4444",
  aguardando_assinatura: "#0891B2", aguardando_faturamento: "#4F46E5", faturada: "#64748B", cancelada: "#F97316",
};
const STATUS_HEX_FALLBACK = "#94A3B8";

// Paleta discreta para os gráficos "por cliente" (barras horizontais) —
// UMA cor fixa por gráfico em todas as barras, sem inventar uma cor por
// cliente (nenhuma correspondência semântica a manter). Cores ajustadas
// no COMMIT D (delta 2026-09-18) para bater com a referência visual
// aprovada — roxo (Horas por cliente) e verde escuro (Faturamento por
// cliente), nunca a mesma cor dos outros gráficos (evita confundir qual
// card é qual ao passar o olho pela grade).
const CLIENT_BAR_COLOR = "#8B5CF6";
const CLIENT_BAR_COLOR_SECONDARY = "#047857";
// Faturamento por mês (área) e Evolução de recebimentos (linha) — mesmo
// motivo acima, cor própria e fixa, batendo com a referência.
const FATURAMENTO_COLOR = "#0D9488";
const RECEBIMENTOS_COLOR = "#F97316";
const HORAS_REALIZADAS_COLOR = "#0EA5E9";
const HORAS_FATURADAS_COLOR = "#BAE6FD";

// Datas em pt-BR (item "FORMATAÇÃO" do pedido) — os 3 gráficos de
// tendência mensal (Faturamento por mês / Horas executadas / Evolução de
// recebimentos) recebem a chave "AAAA-MM" já calculada no backend (GET
// /dashboard) ou localmente (trailingMonthKeys, abaixo) — só formatação
// de exibição, nunca uma chave/mês novo inventado.
const MESES_PT_ABREV = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
export function formatMesPt(mesKey) {
  const [ano, mes] = String(mesKey || "").split("-");
  const idx = Number(mes) - 1;
  if (!ano || idx < 0 || idx > 11 || Number.isNaN(idx)) return mesKey || "";
  return `${MESES_PT_ABREV[idx]}/${ano}`;
}

// Melhoria do filtro de período, item 1 — substitui o antigo seletor de
// competência fechado ("Setembro/2026") por um intervalo real de datas
// (data inicial/final), sem limitar a um mês ou ano específico.
const toIsoDate = (dt) => `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
const todayIso = () => toIsoDate(new Date());
const firstOfMonthIso = () => { const n = new Date(); return toIsoDate(new Date(n.getFullYear(), n.getMonth(), 1)); };
const firstOfYearIso = () => { const n = new Date(); return toIsoDate(new Date(n.getFullYear(), 0, 1)); };
const daysAgoIso = (days) => { const n = new Date(); n.setDate(n.getDate() - days); return toIsoDate(n); };

// EXPERIMENTAL (delta 2026-09-17) — mesma lógica de "últimos N meses
// reais" já usada no backend (GET /dashboard, campo `series`) para os
// dois gráficos de tendência existentes; replicada aqui (não há endpoint
// que devolva isso pronto para "Evolução de recebimentos") só como chave
// de agrupamento de mês ("AAAA-MM"), sem nenhuma regra de negócio nova.
function trailingMonthKeys(count) {
  const now = new Date();
  const out = [];
  for (let i = count - 1; i >= 0; i--) {
    let y = now.getFullYear();
    let m = now.getMonth() + 1 - i;
    while (m <= 0) { m += 12; y -= 1; }
    out.push(`${y}-${String(m).padStart(2, "0")}`);
  }
  return out;
}

// Atalhos rápidos (item 3) — nunca substituem os campos de data, só
// preenchem os dois de uma vez; o usuário continua podendo editar
// livremente depois de clicar em qualquer um deles.
const PERIOD_SHORTCUTS = [
  { label: "Hoje", start: () => todayIso(), end: () => todayIso() },
  { label: "Este mês", start: () => firstOfMonthIso(), end: () => todayIso() },
  { label: "Últimos 30 dias", start: () => daysAgoIso(29), end: () => todayIso() },
  { label: "Este ano", start: () => firstOfYearIso(), end: () => todayIso() },
];

export default function Dashboard() {
  const [d, setD] = useState(null);
  const [goals, setGoals] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [clients, setClients] = useState([]);
  // Melhoria do filtro de período, item 2 — comportamento padrão ao abrir
  // a tela: primeiro dia do mês atual até hoje. O usuário pode alterar
  // livremente depois (campos sempre editáveis, item 3).
  const [periodStart, setPeriodStart] = useState(firstOfMonthIso());
  const [periodEnd, setPeriodEnd] = useState(todayIso());
  // Pacote 2, item 8 — filtro de cliente ("" = Todos). Só afeta os KPIs/
  // gráficos/últimas fichas do próprio endpoint /dashboard; Metas, Comissões
  // e Ranking (podium) são indicadores de DESEMPENHO POR ANALISTA agregados
  // em toda a carteira do tenant no período — filtrar por um único
  // cliente não é conceitualmente coerente para eles (a meta/comissão de um
  // analista não é "por cliente"), então continuam sempre no total do
  // tenant, com um aviso na tela quando um cliente está selecionado.
  // Decisão documentada também no relatório do pacote.
  const [clienteId, setClienteId] = useState("");

  // Melhoria do filtro de período, item 4 — validação simples e em
  // português; nunca dispara a consulta com um intervalo inválido (isso
  // deve retornar estado vazio normal quando não há registros, não um
  // erro de validação).
  const periodInvalid = periodStart && periodEnd && periodStart > periodEnd;

  // EXPERIMENTAL (delta 2026-09-17) — dados dos 3 gráficos "do período"
  // (Distribuição por status / Horas por cliente / Faturamento por
  // cliente). Reaproveita EXATAMENTE os endpoints que Relatórios já usa
  // (GET /reports/services|hours|invoicing) — mesmo filtro
  // cliente/período do Dashboard, sem duplicar nenhuma regra de negócio
  // nem criar endpoint novo; a agregação (contar por status, somar por
  // cliente, top 5) é feita aqui, só para exibição. Cada gráfico guarda
  // seu próprio loading/erro (item 12) — uma falha num não derruba os
  // outros.
  const [statusDist, setStatusDist] = useState({ loading: true, error: false, data: [] });
  const [horasPorCliente, setHorasPorCliente] = useState({ loading: true, error: false, data: [] });
  const [faturamentoPorCliente, setFaturamentoPorCliente] = useState({ loading: true, error: false, data: [] });
  // "Evolução de recebimentos" — mesmo espírito das séries de 6 meses já
  // existentes (Horas por mês / Faturamento mensal, GET /dashboard):
  // tendência fixa dos últimos 6 meses, respeitando o filtro de cliente
  // mas não o período pontual (mesmo comportamento já aprovado de
  // `d.series`, GET /dashboard, série sempre "últimos 6 meses reais").
  // GET /reports/boletos já resolve cliente_nome e devolve `pago_em` —
  // "recebido" é SEMPRE boleto com status "pago" (nunca em_aberto/
  // vencido), a única definição real de "recebido" no LOOTUS.
  const [recebimentos, setRecebimentos] = useState({ loading: true, error: false, data: [] });

  // Correções de homologação (P1) — nenhuma chamada protegida pode ficar
  // sem tratamento de erro: uma rejeição sem .catch aqui virava promise
  // rejeitada sem tratamento (o próprio "Uncaught runtime errors" do 403
  // financeiro observado na homologação). O redirecionamento em si já é
  // resolvido antes disso (AuthContext resolve /auth/me antes de navegar,
  // ProtectedRoute nunca deixa este componente montar para uma empresa
  // bloqueada) — este .catch é a segunda camada de defesa.
  useEffect(() => {
    api.get("/clients").then(r => setClients(r.data.filter(c => c.active !== false))).catch(() => {});
  }, []);

  useEffect(() => {
    if (periodInvalid || !periodStart || !periodEnd) return;
    const params = { start_date: periodStart, end_date: periodEnd, cliente_id: clienteId };
    api.get("/dashboard", { params }).then(r => setD(r.data)).catch(() => {});
    api.get("/dashboard/goals", { params: { start_date: periodStart, end_date: periodEnd } }).then(r => setGoals(r.data)).catch(() => {});
    api.get("/dashboard/commissions", { params: { start_date: periodStart, end_date: periodEnd } }).then(r => setCommissions(r.data)).catch(() => {});
    // eslint-disable-next-line
  }, [periodStart, periodEnd, periodInvalid, clienteId]);

  useEffect(() => {
    if (periodInvalid || !periodStart || !periodEnd) return;
    const reportParams = { cliente_id: clienteId, start: periodStart, end: periodEnd };

    setStatusDist(s => ({ ...s, loading: true, error: false }));
    api.get("/reports/services", { params: reportParams }).then(r => {
      const counts = {};
      for (const t of r.data) counts[t.status] = (counts[t.status] || 0) + 1;
      const rows = Object.entries(counts).map(([status, value]) => ({ status, label: statusPt(status), value, color: STATUS_HEX[status] || STATUS_HEX_FALLBACK }));
      setStatusDist({ loading: false, error: false, data: rows });
    }).catch(() => setStatusDist({ loading: false, error: true, data: [] }));

    setHorasPorCliente(s => ({ ...s, loading: true, error: false }));
    api.get("/reports/hours", { params: reportParams }).then(r => {
      const agg = {};
      for (const row of r.data) {
        const nome = row.cliente || "Sem cliente";
        agg[nome] = (agg[nome] || 0) + (row.horas_realizadas || 0);
      }
      const rows = Object.entries(agg).map(([cliente, horas]) => ({ cliente, horas: Math.round(horas * 100) / 100 }))
        .sort((a, b) => b.horas - a.horas).slice(0, 5);
      setHorasPorCliente({ loading: false, error: false, data: rows });
    }).catch(() => setHorasPorCliente({ loading: false, error: true, data: [] }));

    setFaturamentoPorCliente(s => ({ ...s, loading: true, error: false }));
    api.get("/reports/invoicing", { params: reportParams }).then(r => {
      const agg = {};
      for (const inv of r.data) {
        const nome = inv.cliente_nome || "Sem cliente";
        agg[nome] = (agg[nome] || 0) + (inv.valor_total || 0);
      }
      const rows = Object.entries(agg).map(([cliente, valor]) => ({ cliente, valor: Math.round(valor * 100) / 100 }))
        .sort((a, b) => b.valor - a.valor).slice(0, 5);
      setFaturamentoPorCliente({ loading: false, error: false, data: rows });
    }).catch(() => setFaturamentoPorCliente({ loading: false, error: true, data: [] }));
  }, [periodStart, periodEnd, periodInvalid, clienteId]);

  useEffect(() => {
    const meses = trailingMonthKeys(6);
    setRecebimentos(s => ({ ...s, loading: true, error: false }));
    api.get("/reports/boletos", { params: { cliente_id: clienteId, status: "pago" } }).then(r => {
      const porMes = Object.fromEntries(meses.map(m => [m, 0]));
      for (const b of r.data) {
        const key = (b.pago_em || "").slice(0, 7);
        if (key in porMes) porMes[key] += b.valor || 0;
      }
      setRecebimentos({ loading: false, error: false, data: meses.map(m => ({ mes: m, recebido: Math.round(porMes[m] * 100) / 100 })) });
    }).catch(() => setRecebimentos({ loading: false, error: true, data: [] }));
  }, [clienteId]);

  const applyShortcut = (s) => { setPeriodStart(s.start()); setPeriodEnd(s.end()); };

  // "Limpar filtros" (item 11 do pedido — Dashboard tem filtro real de
  // Cliente/Data inicial/Data final) — volta ao MESMO padrão coerente já
  // usado na abertura da tela (item 2 do filtro de período original:
  // primeiro dia do mês até hoje), nunca datas vazias (quebraria a
  // consulta — periodInvalid/!periodStart já trata isso como "não
  // buscar").
  const limparFiltrosDashboard = () => {
    setClienteId(""); setPeriodStart(firstOfMonthIso()); setPeriodEnd(todayIso());
  };

  if (!d) return <div className="text-slate-500">Carregando...</div>;
  const k = d.kpis;
  const periodLabel = `${fmtDate(periodStart)} a ${fmtDate(periodEnd)}`;
  const clienteLabel = clienteId ? (clients.find(c => c.id === clienteId)?.razao_social || "") : "";
  const top3 = goals.filter(g => g.faturado_mes > 0).slice(0, 3);
  const podium = [
    { g: top3[0], icon: Trophy, color: "bg-amber-100 text-amber-700 border-amber-300", label: "1º" },
    { g: top3[1], icon: Medal, color: "bg-slate-100 text-slate-700 border-slate-300", label: "2º" },
    { g: top3[2], icon: Award, color: "bg-orange-100 text-orange-700 border-orange-300", label: "3º" },
  ].filter(p => p.g);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold">Dashboard</h1>
          <p className="text-slate-500 text-sm">Resumo de {periodLabel}{clienteLabel && <> · {clienteLabel}</>}</p>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <div className="flex items-end gap-2 flex-wrap">
            <div className="w-48">
              <Select value={clienteId || "todos"} onValueChange={v => setClienteId(v === "todos" ? "" : v)}>
                <SelectTrigger data-testid="dashboard-cliente"><SelectValue placeholder="Cliente" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos os clientes</SelectItem>
                  {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.razao_social}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Data inicial</Label>
              <Input type="date" className="w-40" value={periodStart} onChange={e => setPeriodStart(e.target.value)} data-testid="dashboard-data-inicial" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Data final</Label>
              <Input type="date" className="w-40" value={periodEnd} onChange={e => setPeriodEnd(e.target.value)} data-testid="dashboard-data-final" />
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <CalendarDays size={14} className="text-slate-400" />
            {PERIOD_SHORTCUTS.map(s => (
              <Button key={s.label} type="button" variant="outline" size="sm" className="h-7 px-2 text-xs"
                      onClick={() => applyShortcut(s)} data-testid={`dashboard-atalho-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
                {s.label}
              </Button>
            ))}
            <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={limparFiltrosDashboard} data-testid="dashboard-limpar-filtros">
              <X size={13} /> Limpar filtros
            </Button>
          </div>
          {periodInvalid && (
            <p className="text-xs text-red-600" data-testid="dashboard-periodo-invalido">A data inicial não pode ser posterior à data final.</p>
          )}
        </div>
      </div>

      {podium.length > 0 && (
        <Card className="p-5 bg-slate-50 border-slate-200" data-testid="ranking-mensal">
          <div className="flex items-center gap-2 mb-1"><Trophy size={18} className="text-amber-600" /><div className="font-heading font-semibold">Ranking do mês</div></div>
          <p className="text-xs text-slate-500 mb-3">{clienteId ? "Sempre considera todos os clientes do tenant, independentemente do filtro de cliente acima." : " "}</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {podium.map(({ g, icon: Icon, color, label }, i) => (
              <div key={g.id} data-testid={`podium-${i+1}`} className={`p-4 rounded-lg border ${color} flex items-center gap-3`}>
                <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center border border-current">
                  <Icon size={22} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs uppercase tracking-wider font-semibold opacity-80">{label} lugar</div>
                  <div className="font-heading font-bold text-slate-900 truncate">{g.name}</div>
                  <div className="text-sm text-slate-700"><b>{money(g.faturado_mes)}</b> {g.meta_mensal > 0 && <span className="text-xs opacity-70">· {g.percent}% da meta</span>}</div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard testid="kpi-servicos" label="Serviços no mês" value={k.servicos_mes} icon={FileText} tone="blue" />
        <KpiCard testid="kpi-horas-realizadas" label="Horas realizadas" value={fmtHours(k.horas_realizadas)} icon={Clock} tone="indigo" />
        <KpiCard testid="kpi-horas-faturadas" label="Horas faturadas" value={fmtHours(k.horas_faturadas)} icon={Timer} tone="emerald" />
        <KpiCard testid="kpi-valor-faturado" label="Valor faturado" value={money(k.valor_faturado)} icon={Wallet} tone="emerald" />
        <KpiCard testid="kpi-valor-nao-faturado" label="Não faturado" value={money(k.valor_nao_faturado)} icon={TrendingUp} tone="amber" />
        <KpiCard testid="kpi-boletos-abertos" label="Boletos em aberto" value={k.boletos_abertos} icon={Receipt} tone="blue" />
        <KpiCard testid="kpi-boletos-pagos" label="Boletos pagos" value={k.boletos_pagos} icon={CheckCircle2} tone="emerald" />
        <KpiCard testid="kpi-boletos-vencidos" label="Boletos vencidos" value={k.boletos_vencidos} icon={AlertTriangle} tone="red" />
      </div>

      {/* EXPERIMENTAL — Dashboard analytics (delta 2026-09-17). Grade
          responsiva: 3 colunas no desktop, 2 no tablet, 1 no mobile (item
          9 do pedido). Os 2 primeiros cards (Faturamento por mês / Horas
          executadas) substituem visualmente os antigos "Faturamento
          mensal"/"Horas por mês" (MESMOS dados, `d.series`, GET
          /dashboard — nenhuma métrica nova); os 4 seguintes são novos,
          cada um com seus próprios loading/vazio/erro (ChartCard). */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <ChartCard icon={TrendingUp} title="Faturamento por mês" subtitle="Últimos 6 meses" testId="chart-faturamento" loading={false} error={false} empty={d.series.every(m => !m.faturamento)} emptyMessage="Sem dados no período.">
          <ResponsiveContainer>
            <AreaChart data={d.series} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
              <defs>
                <linearGradient id="dashFaturamentoGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={FATURAMENTO_COLOR} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={FATURAMENTO_COLOR} stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F7" vertical={false} />
              <XAxis dataKey="mes" tickFormatter={formatMesPt} stroke="#94A3B8" fontSize={11} axisLine={false} tickLine={false} />
              <YAxis stroke="#94A3B8" fontSize={11} axisLine={false} tickLine={false} tickFormatter={v => `${v / 1000}k`} />
              <Tooltip formatter={v => money(v)} labelFormatter={formatMesPt} />
              <Area type="monotone" dataKey="faturamento" name="Faturamento" stroke={FATURAMENTO_COLOR} strokeWidth={2} fill="url(#dashFaturamentoGradient)" />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard icon={BarChart3} title="Horas executadas" subtitle="Por período" testId="chart-horas" loading={false} error={false} empty={d.series.every(m => !m.horas_realizadas && !m.horas_faturadas)} emptyMessage="Sem dados no período.">
          <ResponsiveContainer>
            <BarChart data={d.series} margin={{ top: 4, right: 4, left: -16, bottom: 0 }} barGap={2}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F7" vertical={false} />
              <XAxis dataKey="mes" tickFormatter={formatMesPt} stroke="#94A3B8" fontSize={11} axisLine={false} tickLine={false} />
              <YAxis stroke="#94A3B8" fontSize={11} axisLine={false} tickLine={false} />
              <Tooltip formatter={v => fmtHours(v)} labelFormatter={formatMesPt} />
              <Legend wrapperStyle={{ fontSize: 11 }} iconSize={8} />
              <Bar dataKey="horas_realizadas" name="Realizadas" fill={HORAS_REALIZADAS_COLOR} radius={[3, 3, 0, 0]} maxBarSize={22} />
              <Bar dataKey="horas_faturadas" name="Faturadas" fill={HORAS_FATURADAS_COLOR} radius={[3, 3, 0, 0]} maxBarSize={22} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard icon={PieChartIcon} title="Distribuição por status" subtitle={periodLabel} testId="chart-status"
                   loading={statusDist.loading} error={statusDist.error} empty={!statusDist.loading && statusDist.data.length === 0}
                   emptyMessage="Sem dados no período.">
          <ResponsiveContainer>
            <PieChart>
              <Pie data={statusDist.data} dataKey="value" nameKey="label" innerRadius={42} outerRadius={64} paddingAngle={2}>
                {statusDist.data.map((entry) => <Cell key={entry.status} fill={entry.color} />)}
              </Pie>
              <Tooltip formatter={(v, n) => [`${v} ficha${v === 1 ? "" : "s"}`, n]} />
              <Legend wrapperStyle={{ fontSize: 10.5 }} iconSize={8} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard icon={Users} title="Horas por cliente" subtitle={`Top 5 · ${periodLabel}`} testId="chart-horas-cliente"
                   loading={horasPorCliente.loading} error={horasPorCliente.error} empty={!horasPorCliente.loading && horasPorCliente.data.length === 0}
                   emptyMessage="Sem dados no período.">
          <ResponsiveContainer>
            <BarChart data={horasPorCliente.data} layout="vertical" margin={{ top: 4, right: 12, left: 4, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F7" horizontal={false} />
              <XAxis type="number" stroke="#94A3B8" fontSize={11} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="cliente" stroke="#94A3B8" fontSize={11} width={92} tick={{ width: 88 }} axisLine={false} tickLine={false} />
              <Tooltip formatter={v => fmtHours(v)} />
              <Bar dataKey="horas" name="Horas" fill={CLIENT_BAR_COLOR} radius={[0, 3, 3, 0]} barSize={16} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard icon={Building2} title="Faturamento por cliente" subtitle={`Top 5 · ${periodLabel}`} testId="chart-faturamento-cliente"
                   loading={faturamentoPorCliente.loading} error={faturamentoPorCliente.error} empty={!faturamentoPorCliente.loading && faturamentoPorCliente.data.length === 0}
                   emptyMessage="Sem dados no período.">
          <ResponsiveContainer>
            <BarChart data={faturamentoPorCliente.data} layout="vertical" margin={{ top: 4, right: 12, left: 4, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F7" horizontal={false} />
              <XAxis type="number" stroke="#94A3B8" fontSize={11} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="cliente" stroke="#94A3B8" fontSize={11} width={92} tick={{ width: 88 }} axisLine={false} tickLine={false} />
              <Tooltip formatter={v => money(v)} />
              <Bar dataKey="valor" name="Faturamento" fill={CLIENT_BAR_COLOR_SECONDARY} radius={[0, 3, 3, 0]} barSize={16} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard icon={Activity} title="Evolução de recebimentos" subtitle="Boletos pagos · últimos 6 meses" testId="chart-recebimentos"
                   loading={recebimentos.loading} error={recebimentos.error} empty={!recebimentos.loading && recebimentos.data.every(m => !m.recebido)}
                   emptyMessage="Sem dados no período.">
          <ResponsiveContainer>
            <LineChart data={recebimentos.data} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F7" vertical={false} />
              <XAxis dataKey="mes" tickFormatter={formatMesPt} stroke="#94A3B8" fontSize={11} axisLine={false} tickLine={false} />
              <YAxis stroke="#94A3B8" fontSize={11} axisLine={false} tickLine={false} tickFormatter={v => `${v / 1000}k`} />
              <Tooltip formatter={v => money(v)} labelFormatter={formatMesPt} />
              <Line type="monotone" dataKey="recebido" name="Recebido" stroke={RECEBIMENTOS_COLOR} strokeWidth={2} dot={{ r: 3, fill: RECEBIMENTOS_COLOR }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <Card className="p-5" data-testid="ultimas-fichas">
        <div className="font-heading font-semibold mb-4">Últimas fichas</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500 border-b border-slate-200">
                <th className="py-2 px-2">Ficha</th><th>Data</th><th>Serviço</th><th>Horas</th><th>Status</th>
              </tr>
            </thead>
            <tbody>
              {d.ultimas_fichas.map(t => (
                <tr key={t.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="py-2 px-2 font-medium">#{t.numero}</td>
                  <td>{fmtDate(t.data_servico)}</td>
                  <td>{t.tipo_servico}</td>
                  <td>{fmtHours(t.horas_realizadas)}</td>
                  <td><Badge className={statusColor(t.status)}>{statusPt(t.status)}</Badge></td>
                </tr>
              ))}
              {d.ultimas_fichas.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-slate-400">Sem fichas ainda</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      {goals.length > 0 && (
        <Card className="p-5" data-testid="metas-analistas">
          <div className="flex items-center gap-2 mb-1"><Target size={18} className="text-primary" /><div className="font-heading font-semibold">Metas — Faturamento do mês por analista</div></div>
          {clienteId && <p className="text-xs text-slate-500 mb-3">Sempre considera todos os clientes do tenant, independentemente do filtro de cliente acima.</p>}
          <div className={`space-y-4 ${clienteId ? "" : "mt-3"}`}>
            {goals.map(g => {
              const pct = g.percent ?? 0;
              const barColor = pct >= 100 ? "bg-emerald-500" : pct >= 70 ? "bg-blue-500" : pct >= 40 ? "bg-amber-500" : "bg-slate-400";
              return (
                <div key={g.id} data-testid={`goal-${g.id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                    <div>
                      <span className="font-medium text-slate-900">{g.name}</span>
                      <span className="text-xs text-slate-500 ml-2 capitalize">{g.role}</span>
                    </div>
                    <div className="text-slate-600">
                      <b className="text-slate-900">{money(g.faturado_mes)}</b>
                      {g.meta_mensal > 0 && <span className="text-slate-400"> / {money(g.meta_mensal)}</span>}
                      {g.percent != null && <Badge className={`ml-2 ${pct >= 100 ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-700"}`}>{pct}%</Badge>}
                      {g.meta_mensal === 0 && <span className="text-xs text-slate-400 ml-2">(sem meta)</span>}
                    </div>
                  </div>
                  <div className="mt-1.5 h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className={`h-full ${barColor} transition-all duration-500`} style={{ width: `${Math.min(100, pct)}%` }} />
                  </div>
                  {g.categorias && g.categorias.length > 0 && (
                    <div className="mt-2 ml-2 pl-3 border-l-2 border-slate-100 space-y-1.5">
                      {g.categorias.map(c => {
                        const cpct = c.percent ?? 0;
                        const cbar = cpct >= 100 ? "bg-emerald-400" : cpct >= 70 ? "bg-blue-400" : cpct >= 40 ? "bg-amber-400" : "bg-slate-300";
                        return (
                          <div key={c.categoria} className="text-xs">
                            <div className="flex justify-between text-slate-500">
                              <span>· {c.categoria}</span>
                              <span>{money(c.faturado)}{c.meta > 0 && <> / {money(c.meta)} · <b className={cpct >= 100 ? "text-emerald-700" : "text-slate-700"}>{c.percent}%</b></>}</span>
                            </div>
                            {c.meta > 0 && (
                              <div className="mt-0.5 h-1 bg-slate-100 rounded-full overflow-hidden">
                                <div className={`h-full ${cbar} transition-all duration-500`} style={{ width: `${Math.min(100, cpct)}%` }} />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      )}
      {commissions.length > 0 && (
        <Card className="p-5" data-testid="comissoes">
          <div className="flex items-center gap-2 mb-1"><Percent size={18} className="text-emerald-600" /><div className="font-heading font-semibold">Comissões do mês</div></div>
          {clienteId && <p className="text-xs text-slate-500 mb-3">Sempre considera todos os clientes do tenant, independentemente do filtro de cliente acima.</p>}
          <div className={`overflow-x-auto ${clienteId ? "" : "mt-3"}`}>
            <table className="w-full text-sm">
              <thead className="text-left text-slate-500 border-b border-slate-200">
                <tr><th className="py-2 px-2">Analista</th><th>Base</th><th>Regra</th><th className="text-right pr-2">Comissão</th></tr>
              </thead>
              <tbody>
                {commissions.map(c => {
                  const hourly = c.commission_mode === "fixed_hourly";
                  const fmtRate = (r) => hourly ? `${money(r)}/h` : `${r}%`;
                  const regra = c.categorias.map(x => `${x.categoria}: ${fmtRate(x.rate ?? x.pct)}`).join(" · ")
                    || `padrão ${fmtRate(c.comissao_padrao)}`;
                  return (
                    <tr key={c.id} className="border-b border-slate-100">
                      <td className="py-2 px-2 font-medium">{c.name}</td>
                      <td>{hourly ? fmtHours(c.horas_total) : money(c.faturado_total)}</td>
                      <td className="text-xs text-slate-500">{regra}</td>
                      <td className="text-right pr-2 font-heading font-bold text-emerald-700">{money(c.comissao_total)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
